import { Routes } from '@angular/router';
import { UsersComponent } from './admin/users/users.component';
import { ScannersComponent } from './admin/scanners/scanners.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component';
import { CallbackComponent } from './callback/callback.component';
import { DataAgreementsComponent } from './data-agreements/data-agreements.component';
import { AuthService, AdminAuthGuard, AdminOrClientAdminAuthGuard } from './@services/auth.service';
import { PendingDeletionsComponent } from './admin/pending-deletions/pending-deletions.component';
import { CustomerDirectoryComponent } from './admin/customer-directory/customer-directory.component';
import { CustomerAssetsComponent } from './admin/customer-assets/customer-assets.component';
import { SsoTesterComponent } from './admin/sso-tester/sso-tester.component';
import { BillingComponent } from './admin/billing/billing.component';
import { UserTrackingComponent } from './admin/user-tracking/user-tracking.component';
import { UserActivityComponent } from './admin/user-activity/user-activity.component';
import { BillingReportsComponent } from './admin/billing-reports/billing-reports.component';
import { BillingBreakoutComponent } from './admin/billing-reports/billing-breakout.component';
import { ExchangeAgreementsComponent } from './admin/exchange-agreements/exchange-agreements.component';
import { ComplianceReportComponent } from './admin/compliance-report/compliance-report.component';
import { ExchangeAgreementReviewComponent } from './admin/exchange-agreement-review/exchange-agreement-review.component';
import { TheoTradeBillingComponent } from './admin/theo-trade-billing/theo-trade-billing.component';
import { TheoTradeBillingExternalComponent } from './admin/theo-trade-billing-external/theo-trade-billing-external.component';
import { UtilitiesComponent } from './admin/utilities/utilities.component';
import { ClientNotificationsComponent } from './admin/client-notifications/client-notifications.component';
import { ErrorReportsComponent } from './admin/error-reports/error-reports.component';
import { ServicesComponent } from './admin/services/services.component';
import { CommerceEventsComponent } from './admin/commerce-events/commerce-events.component';
import { CommonDataComponent } from './admin/common-data/common-data.component';
import { ManagePinsComponent } from './admin/pinned/manage-pins.component';
import { PinnedIframeComponent } from './admin/pinned/pinned-iframe.component';
import { NotFound404Component } from './not-found-404/not-found-404.component';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home'
  },
  {
    path: 'login',
    // No title — AppTitleStrategy renders just "F2 Admin" when the route
    // has no panel-specific title. Mike's left-nav-pattern spec.
    component: LoginComponent
  },
  {
    path: 'callback',
    title: 'Signing in…',
    component: CallbackComponent
  },
  {
    path: 'home',
    title: 'Dashboard',
    component: HomeComponent,
    canActivate: [AuthService]
  },
  {
    path: 'data-agreements',
    title: 'Live Data Agreements',
    component: DataAgreementsComponent,
    canActivate: [AuthService],
    data: { standalone: true }
  },
  // NOTE: /client/users (ClientUsersComponent) was removed 2026-07-02.
  // The client-slim surface now lives entirely in the separate
  // f2-clients SPA (clients.f2-tech.<tld>). Any client-role user who
  // still hits the admin app is bounced out by AuthService.canActivate.
  {
    path: 'admin',
    // No title on the parent — AppTitleStrategy appends " - F2 Admin"
    // to whichever child's title is deepest. A parent title would
    // shadow the child and we'd lose the panel name.
    canActivate: [AuthService, AdminOrClientAdminAuthGuard],
    children: [
      {
        path: 'users',
        title: 'Users',
        component: UsersComponent,
        // client_admin allowed here — backend scopes the response to
        // their custom:customers. Strict admin-only sub-routes below
        // keep AdminAuthGuard.
        canActivate: [AuthService, AdminOrClientAdminAuthGuard]
      },
      // IT-F2-106 Phase 4a — deep-linkable Add-User / Edit-User drawer.
      // Same UsersComponent (drawer is an overlay on the users grid).
      // Route param drives which drawer opens. `View full editor` links
      // on the row-action modals navigate to /admin/users/:username.
      {
        path: 'users/new',
        title: 'Add User',
        component: UsersComponent,
        canActivate: [AuthService, AdminOrClientAdminAuthGuard]
      },
      {
        path: 'users/:username',
        title: 'Edit User',
        component: UsersComponent,
        canActivate: [AuthService, AdminOrClientAdminAuthGuard]
      },
      {
        path: 'pending-deletions',
        title: 'Pending Deletions',
        component: PendingDeletionsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'customer-directory',
        title: 'Customer Directory',
        component: CustomerDirectoryComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'customer-assets',
        title: 'Customer Assets',
        component: CustomerAssetsComponent,
        // Same gate as Users — client_admin can see their own customers'
        // assets (server filters); admin sees everything.
        canActivate: [AuthService, AdminOrClientAdminAuthGuard]
      },
      {
        path: 'scanners',
        title: 'Scanners',
        component: ScannersComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'common-data',
        title: 'Common Data',
        component: CommonDataComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        // Legacy path — kept as a redirect so old bookmarks + KG links
        // survive the 2026-07-16 graduation of the symbol-lists panel
        // into the multi-source /admin/common-data hub.
        path: 'common-symbol-lists',
        redirectTo: 'common-data',
        pathMatch: 'full'
      },
      {
        // Manage the personal pin list. `:key`-suffixed sibling routes
        // render the iframe. AdminOrClientAdmin scope — pins are per-
        // user prefs and useful for both admin roles.
        path: 'pinned',
        title: 'Pinned scanners',
        component: ManagePinsComponent,
        canActivate: [AuthService]
      },
      {
        path: 'pinned/:key',
        title: 'Pinned scanner',
        component: PinnedIframeComponent,
        canActivate: [AuthService]
      },
      {
        path: 'sso-tester',
        title: 'SSO Tester',
        component: SsoTesterComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'error-reports',
        title: 'Error Reports',
        component: ErrorReportsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'services',
        title: 'Services',
        component: ServicesComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'error-reports/:id',
        title: 'Error Reports',
        component: ErrorReportsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'commerce-events',
        title: 'Commerce Events',
        component: CommerceEventsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'commerce-events/:id',
        title: 'Commerce Events',
        component: CommerceEventsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'billing',
        title: 'Billing',
        component: BillingComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'tracking',
        title: 'User Tracking',
        component: UserTrackingComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'activity',
        title: 'User Activity',
        component: UserActivityComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'billing-reports',
        title: 'Billing Reports',
        component: BillingReportsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'billing-reports/breakout',
        title: 'Billing Breakout',
        component: BillingBreakoutComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'exchange-agreements',
        title: 'Exchange Agreements',
        component: ExchangeAgreementsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'compliance-report',
        title: 'Compliance Report',
        component: ComplianceReportComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        // IT-F2-391 c/a4c363b1 — spun out of Compliance Report as its
        // own panel. Mirrors the admin-only guard from compliance-report
        // above; this surface only makes sense to strict admins.
        path: 'exchange-agreement-review',
        title: 'Exchange Agreement Review',
        component: ExchangeAgreementReviewComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        // IT-F2-426 c/e12385a8 (Mike 2026-10-02) — admin uploads a
        // signed PDF and the standard questionnaire fields get
        // prefilled from parse-pdf. Same form shape as the regular
        // user flow at /data-agreements, so there's one source of
        // truth for the agreement doc structure. Admin-only.
        path: 'exchange-agreement-review/add-from-pdf',
        title: 'Add Exchange Agreement from PDF',
        loadComponent: () => import('./admin/exchange-agreement-add/exchange-agreement-add.component').then(m => m.ExchangeAgreementAddComponent),
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'theotrade-billing',
        title: 'TheoTrade Billing',
        component: TheoTradeBillingComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'theotrade-billing-jr',
        title: 'TheoTrade Billing (External)',
        component: TheoTradeBillingExternalComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'utilities',
        title: 'Utilities',
        component: UtilitiesComponent,
        canActivate: [AuthService, AdminAuthGuard]
      },
      {
        path: 'client-notifications',
        title: 'Client Notifications',
        component: ClientNotificationsComponent,
        canActivate: [AuthService, AdminAuthGuard]
      }
    ]
  },
  {
    path: '**',
    pathMatch: 'full',
    component: NotFound404Component
  }
];
